import { useQuery } from '@tanstack/react-query';
import { DocumentsService } from 'api';
import {
  FIRM_DOCUMENT_SUGGESTIONS_QUERY_KEY,
  FIRM_DOCUMENT_SUGGESTIONS_QUERY_LIMIT,
  FIRM_DOCUMENT_SUGGESTIONS_QUERY_MIN_SEARCH_LENGTH,
  FIRM_DOCUMENT_SUGGESTIONS_QUERY_STALE_TIME_MS,
} from './useFirmDocumentSuggestionsQuery.constants';
import type { UseFirmDocumentSuggestionsQueryParams } from './useFirmDocumentSuggestionsQuery.types';
import { getNormalizedSearch } from '../documentsQuery.utils';

type FirmDocumentSuggestionsFileTypeParam =
  Parameters<typeof DocumentsService.apiDocumentsFirmsDocumentsSuggestionsList>[6];
type FirmDocumentSuggestionsUploadedByParam =
  Parameters<typeof DocumentsService.apiDocumentsFirmsDocumentsSuggestionsList>[5];
type FirmDocumentSuggestionsSourceParam =
  Parameters<typeof DocumentsService.apiDocumentsFirmsDocumentsSuggestionsList>[7];
type FirmDocumentSuggestionsReferenceObjectTypeParam =
  Parameters<typeof DocumentsService.apiDocumentsFirmsDocumentsSuggestionsList>[8];

const getUploadedByParam = (uploadedBy?: string | null) => {
  const uploadedById = Number(uploadedBy);

  return (uploadedById > 0 ? uploadedById : undefined) as FirmDocumentSuggestionsUploadedByParam;
};
const getFileTypeParam = (fileType?: string | null) => (
  fileType || undefined
) as FirmDocumentSuggestionsFileTypeParam;
const getSourceParam = (source?: string | null) => (
  source || undefined
) as FirmDocumentSuggestionsSourceParam;
const getReferenceObjectTypeParam = (referenceObjectType?: string | null) => (
  referenceObjectType || undefined
) as FirmDocumentSuggestionsReferenceObjectTypeParam;

export const useFirmDocumentSuggestionsQuery = ({
  companyId = null,
  enabled = true,
  fileType = null,
  firmId,
  fundId = null,
  limit = FIRM_DOCUMENT_SUGGESTIONS_QUERY_LIMIT,
  preferredMeasurementDate = null,
  referenceObjectType = null,
  search,
  source = null,
  uploadedBy = null,
}: UseFirmDocumentSuggestionsQueryParams) => {
  const normalizedSearch = getNormalizedSearch(search);
  const isSearchReady = Boolean(
    normalizedSearch && normalizedSearch.length >= FIRM_DOCUMENT_SUGGESTIONS_QUERY_MIN_SEARCH_LENGTH
  );

  return useQuery({
    enabled: Boolean(firmId) && enabled && isSearchReady,
    queryFn: () =>
      DocumentsService.apiDocumentsFirmsDocumentsSuggestionsList(
        String(firmId),
        normalizedSearch ?? '',
        companyId ?? undefined,
        preferredMeasurementDate ?? undefined,
        fundId ?? undefined,
        getUploadedByParam(uploadedBy),
        getFileTypeParam(fileType),
        getSourceParam(source),
        getReferenceObjectTypeParam(referenceObjectType),
        limit
      ),
    queryKey: [
      FIRM_DOCUMENT_SUGGESTIONS_QUERY_KEY,
      firmId,
      companyId ?? '',
      preferredMeasurementDate ?? '',
      fundId ?? '',
      fileType ?? '',
      source ?? '',
      uploadedBy ?? '',
      referenceObjectType ?? '',
      limit,
      normalizedSearch ?? '',
    ],
    staleTime: FIRM_DOCUMENT_SUGGESTIONS_QUERY_STALE_TIME_MS,
  });
};
